'use client';

import Avatar from '@/components/next-ui/Avatar';
import useInfoModalStore from '@/components/store/infoModalSlice';
import { NEKI_LOGO_S3_URL } from '@/constants/neki.constant';
import OrgType from '@/constants/orgType.constant';
import { CommunityRoles } from '@/utils/communityRoles';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { formatNumberForLocale, getAdminLoginUrl } from '@/utils/stringHelpers';
import Typography from '@ui/Typography';
import { cx } from 'class-variance-authority';
import { kebabCase, last } from 'lodash-es';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { MdInfo } from 'react-icons/md';

function MyCommunity(props) {
  const { community: userActiveCommunity, user, accessToken } = props;
  const isCommunityAdmin = userActiveCommunity?.role === CommunityRoles.Admin;
  const isNonProfitAdmin =
    isCommunityAdmin && userActiveCommunity?.orgType === OrgType.NonProfit;
  const hasPublicProfile = userActiveCommunity?.publicProfile?.enabled;

  const isCompanyCommunity = [OrgType.Business, OrgType.ChangeMakers].includes(
    userActiveCommunity?.orgType,
  );
  const isNonProfitCommunity =
    userActiveCommunity?.orgType === OrgType.NonProfit;

  const hasPublicCompanyProfile = hasPublicProfile && isCompanyCommunity;
  const showHomeAnalytics =
    isCommunityAdmin && (isNonProfitCommunity || hasPublicCompanyProfile);

  const primaryCommunity = userActiveCommunity?.primaryCommunity;

  const nekiAdminLoginUrl = getAdminLoginUrl(user?.email, accessToken);

  const setShowInfoModal = useInfoModalStore((state) => state.setOpen);

  const getNonProfitLink = () => {
    if (isNonProfitAdmin) {
      let slug = hasPublicProfile
        ? last(userActiveCommunity.publicProfile.slug?.split('/'))
        : null;

      if (!slug) {
        slug = `${kebabCase(userActiveCommunity?.name)}-${
          userActiveCommunity?.nonProfit?.id
        }`;
      }
      return `/organization/${slug}`;
    }
    return null;
  };

  const getCommunityLogoUrl = (community) => {
    if (community.logoUrl) {
      return getQualifiedImageUrl(community.logoUrl);
    }
    return NEKI_LOGO_S3_URL;
  };

  const handleShowInfo = () => {
    setShowInfoModal(true, {
      logoUrl: '/assets/images/neki-heart.png',
      title: 'Neki Score',
      name: 'Neki Score',
      description:
        'Your Neki Score reflects the total points your community members earn by taking meaningful action—giving, volunteering, engaging and celebrating goodness.',
    });
  };

  return (
    <div className="relative flex flex-col">
      {primaryCommunity && (
        <div className="relative">
          <div className="flex items-center gap-2">
            <Avatar
              src={getCommunityLogoUrl(primaryCommunity)}
              alt={primaryCommunity.name}
              profile="company"
              className="h-10 w-10"
            />
            <Typography
              title={primaryCommunity.name}
              variant="h3"
              component="span"
              className="truncate text-left">
              {primaryCommunity.name && primaryCommunity.name.length > 16
                ? `${primaryCommunity.name.slice(0, 16)}...`
                : primaryCommunity.name}
            </Typography>
          </div>

          <div className="absolute left-5 top-[64px] h-px w-6 bg-gray-500" />
          <div className="absolute left-5 top-10 h-6 w-px bg-gray-500" />
        </div>
      )}

      {userActiveCommunity && (
        <div className={cx(primaryCommunity && 'ml-12 mt-2')}>
          <div className="flex items-center gap-2">
            <Avatar
              src={getCommunityLogoUrl(userActiveCommunity)}
              alt={userActiveCommunity.name}
              profile="company"
              className="h-10 w-10"
            />
            <Typography
              title={userActiveCommunity.name}
              variant="h3"
              component="span"
              className="truncate text-left">
              {userActiveCommunity.name && userActiveCommunity.name.length > 16
                ? `${userActiveCommunity.name.slice(0, 16)}...`
                : userActiveCommunity.name}
            </Typography>
          </div>

          {/* {isCommunityAdmin && (
            <div className="mt-2 flex flex-col gap-2">
              <div className="items-cente flex justify-center gap-2">
                <Image
                  src="/assets/images/neki-heart.png"
                  width={28}
                  height={28}
                  alt="neki-score"
                  className="h-7 w-7 object-contain"
                />
                <div className="flex w-full items-center gap-2">
                  <Typography
                    variant="button"
                    className="break-words text-center">
                    Neki Score:{' '}
                    {formatNumberForLocale(user?.activeCommunity?.points)}
                  </Typography>

                  <div
                    className="flex items-center justify-center cursor-pointer"
                    onClick={handleShowInfo}>
                    <MdInfo className="h-5 w-5 text-text-disabled" />
                  </div>
                </div>
              </div>
            </div>
          )} */}

          <div className="ml-2 mt-4 flex flex-col items-start gap-2">
            {hasPublicProfile && (
              <Link href={`/${userActiveCommunity.publicProfile.slug}`}>
                <Typography
                  variant="subtitle1"
                  className="hover:text-text-hyperlink">
                  Public Page
                </Typography>
              </Link>
            )}
            {isNonProfitAdmin && (
              <Link href={getNonProfitLink()}>
                <Typography
                  variant="subtitle1"
                  className="hover:text-text-hyperlink">
                  My Non Profit
                </Typography>
              </Link>
            )}
            {isCommunityAdmin && (
              <Typography
                component="a"
                href={nekiAdminLoginUrl}
                variant="subtitle1"
                className="hover:text-text-hyperlink">
                Administration
              </Typography>
            )}
            {showHomeAnalytics && (
              <Link href={`/analytics`}>
                <Typography
                  variant="subtitle1"
                  className="hover:text-text-hyperlink">
                  Analytics
                </Typography>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(MyCommunity);
